import { useState, useMemo } from "react";
import { Building2, Plus, Edit, Trash2, Eye } from "lucide-react";
import { PrequalificationStatusBadge } from "@/components/partners/PrequalificationStatusBadge";
import { RatingDisplay } from "@/components/partners/RatingDisplay";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { DeleteConfirmDialog } from "@/components/ui/delete-confirm-dialog";
import { TableSkeleton } from "@/components/ui/table-skeleton";
import { PaginationControls } from "@/components/ui/pagination-controls";
import { useCompanies, Company, CompanyFormData } from "@/hooks/useCompanies";
import { useCompanyFilters } from "@/hooks/useCompanyFilters";
import { usePartnerSettings } from "@/hooks/usePartnerSettings";
import { CompanyForm } from "@/components/companies/CompanyForm";
import { CompanyFilterBar } from "@/components/companies/CompanyFilterBar";
import { CompanyDetailPanel } from "@/components/companies/CompanyDetailPanel";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { useClientPagination } from "@/hooks/useClientPagination";

export default function Companies() {
  const { companies, loading, createCompany, updateCompany, deleteCompany } = useCompanies();
  const {
    searchQuery,
    setSearchQuery,
    companyTypeFilter,
    setCompanyTypeFilter,
    statusFilter,
    setStatusFilter,
    tradeSpecialtiesFilter,
    setTradeSpecialtiesFilter,
    prequalificationStatusFilter,
    setPrequalificationStatusFilter,
    filteredCompanies,
  } = useCompanyFilters(companies);
  const { settings: partnerSettings } = usePartnerSettings();

  const getSpecialtyName = (specialtyId: string) => {
    if (!partnerSettings?.trade_specialties) return specialtyId;
    const specialty = partnerSettings.trade_specialties.find(s => s.id === specialtyId);
    return specialty?.name || specialtyId;
  };

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [detailCompany, setDetailCompany] = useState<Company | null>(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [detailDefaultTab, setDetailDefaultTab] = useState<string>("overview");

  // Client-side pagination using shared hook
  const {
    currentPage,
    pageSize,
    totalPages,
    paginatedItems: paginatedCompanies,
    setCurrentPage,
    setPageSize,
    hasNextPage,
    hasPreviousPage,
  } = useClientPagination({ items: filteredCompanies });

  const handleCreate = () => {
    setSelectedCompany(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (company: Company) => {
    setSelectedCompany(company);
    setIsFormOpen(true);
  };

  const handleDelete = (company: Company) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (data: CompanyFormData) => {
    // Transform empty string dates to null (explicitly handle whitespace and empty values)
    const sanitizedData = {
      ...data,
      license_expiry_date: data.license_expiry_date?.trim() || null,
      insurance_expiry_date: data.insurance_expiry_date?.trim() || null,
      prequalification_date: data.prequalification_date?.trim() || null,
      prequalification_expiry_date: data.prequalification_expiry_date?.trim() || null,
      last_project_date: data.last_project_date?.trim() || null,
    };
    
    if (selectedCompany) {
      await updateCompany({ id: selectedCompany.id, data: sanitizedData });
    } else {
      await createCompany(sanitizedData);
    }
    setIsFormOpen(false);
  };

  const confirmDelete = async () => {
    if (companyToDelete) {
      await deleteCompany(companyToDelete.id);
      setDeleteDialogOpen(false);
      setCompanyToDelete(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500/10 text-green-500 hover:bg-green-500/20';
      case 'inactive': return 'bg-gray-500/10 text-gray-500 hover:bg-gray-500/20';
      case 'prospect': return 'bg-blue-500/10 text-blue-500 hover:bg-blue-500/20';
      case 'archived': return 'bg-red-500/10 text-red-500 hover:bg-red-500/20';
      default: return 'bg-gray-500/10 text-gray-500 hover:bg-gray-500/20';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'client': return 'bg-primary/10 text-primary hover:bg-primary/20';
      case 'prospect': return 'bg-blue-500/10 text-blue-500 hover:bg-blue-500/20';
      case 'vendor': return 'bg-purple-500/10 text-purple-500 hover:bg-purple-500/20';
      case 'contractor': return 'bg-orange-500/10 text-orange-500 hover:bg-orange-500/20';
      case 'partner': return 'bg-green-500/10 text-green-500 hover:bg-green-500/20';
      default: return 'bg-gray-500/10 text-gray-500 hover:bg-gray-500/20';
    }
  };


  const stats = {
    total: companies.length,
    prospects: companies.filter(c => c.company_type === 'prospect').length,
    active: companies.filter(c => c.status === 'active').length,
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Partners</h1>
              <p className="text-muted-foreground">Manage your partners and relationships</p>
            </div>
          </div>
          <TableSkeleton rows={10} columns={8} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Partners</h1>
          <p className="text-muted-foreground">Manage your partners and relationships</p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Partner
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Partners</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Prospects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.prospects}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
        <CompanyFilterBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          companyTypeFilter={companyTypeFilter}
          setCompanyTypeFilter={setCompanyTypeFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          tradeSpecialtiesFilter={tradeSpecialtiesFilter}
          setTradeSpecialtiesFilter={setTradeSpecialtiesFilter}
          tradeSpecialties={partnerSettings?.trade_specialties || []}
          prequalificationStatusFilter={prequalificationStatusFilter}
          setPrequalificationStatusFilter={setPrequalificationStatusFilter}
        />

      {/* Companies Table */}
      <Card>
        <CardContent className="p-0">
          {paginatedCompanies.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="mx-auto h-12 w-12 text-muted-foreground mb-2" />
              <p className="text-muted-foreground">
                {companies.length === 0 ? "No partners yet. Create your first partner!" : "No partners match your filters."}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Partner Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Trade Specialties</TableHead>
                  <TableHead>Prequalification</TableHead>
                  <TableHead>Overall Rating</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedCompanies.map((company) => (
                  <TableRow 
                    key={company.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      setDetailCompany(company);
                      setDetailDefaultTab("overview");
                      setDetailPanelOpen(true);
                    }}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        {company.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getTypeColor(company.company_type)}>
                        {company.company_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(company.status)}>
                        {company.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {company.trade_specialties && company.trade_specialties.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {company.trade_specialties.slice(0, 2).map((specialtyId) => (
                            <Badge key={specialtyId} variant="outline" className="text-xs">
                              {getSpecialtyName(specialtyId)}
                            </Badge>
                          ))}
                          {company.trade_specialties.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{company.trade_specialties.length - 2}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell
                      onClick={(e) => {
                        e.stopPropagation();
                        setDetailCompany(company);
                        setDetailDefaultTab("documents");
                        setDetailPanelOpen(true);
                      }}
                    >
                      {(company.company_type === 'contractor' || company.company_type === 'vendor') ? (
                        <PrequalificationStatusBadge 
                          status={company.prequalification_status || 'not_started'}
                          expiryDate={company.prequalification_expiry_date}
                        />
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {company.overall_rating ? (
                        <RatingDisplay rating={company.overall_rating} size="sm" />
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDetailCompany(company);
                            setDetailDefaultTab("overview");
                            setDetailPanelOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(company);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(company);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          
          {filteredCompanies.length > 0 && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={filteredCompanies.length}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
            />
          )}
        </CardContent>
      </Card>

      {/* Company Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedCompany ? "Edit Partner" : "Create Partner"}
            </DialogTitle>
          </DialogHeader>
          <CompanyForm
            company={selectedCompany}
            onSubmit={handleSubmit}
            onCancel={() => setIsFormOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={confirmDelete}
        title="Delete Partner"
        description={`Are you sure you want to delete ${companyToDelete?.name}? This will also delete all associated contacts and account plans.`}
      />

      {/* Company Detail Panel */}
      <CompanyDetailPanel
        company={detailCompany}
        open={detailPanelOpen}
        onOpenChange={(open) => {
          setDetailPanelOpen(open);
          if (!open) {
            setDetailDefaultTab("overview");
          }
        }}
        onEdit={(company) => {
          setSelectedCompany(company as any);
          setIsFormOpen(true);
          setDetailPanelOpen(false);
        }}
        defaultTab={detailDefaultTab}
      />
      </div>
    </DashboardLayout>
  );
}
